import type { components } from '@nutians/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api.js';

type SavedMeal = components['schemas']['SavedMeal'];

export const SAVED_MEALS_KEY = ['saved-meals'] as const;
export const RECENTS_KEY = ['recents'] as const;

export function useSavedMeals() {
  return useQuery({
    queryKey: SAVED_MEALS_KEY,
    queryFn: async () => {
      const { data, error } = await api.GET('/me/saved-meals');
      if (error || !data) throw new Error('Failed to load saved meals');
      return data;
    },
    staleTime: 60_000,
  });
}

export function useLogSavedMeal() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, date }: { id: string; date: string }) => {
      const { data, error } = await api.POST('/me/saved-meals/{id}/log', {
        params: { path: { id } },
        body: { date },
      });
      if (error || !data) throw new Error('Failed to log saved meal');
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: RECENTS_KEY });
      void qc.invalidateQueries({ queryKey: SAVED_MEALS_KEY });
    },
  });
}

export function useCreateSavedMeal() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({ name, fromEntryIds }: { name: string; fromEntryIds: string[] }) => {
      const { data, error } = await api.POST('/me/saved-meals', {
        body: { name, kind: 'meal', servings: 1, fromEntryIds },
      });
      if (error || !data) throw new Error('Failed to create saved meal');
      return data as SavedMeal;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: SAVED_MEALS_KEY });
      void qc.invalidateQueries({ queryKey: RECENTS_KEY });
    },
  });
}

export function useUpdateSavedMeal() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      name,
      isFavorite,
    }: {
      id: string;
      name?: string;
      isFavorite?: boolean;
    }) => {
      const { data, error } = await api.PATCH('/me/saved-meals/{id}', {
        params: { path: { id } },
        body: { name, isFavorite },
      });
      if (error || !data) throw new Error('Failed to update saved meal');
      return data as SavedMeal;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: SAVED_MEALS_KEY });
      void qc.invalidateQueries({ queryKey: RECENTS_KEY });
    },
  });
}

export function useDeleteSavedMeal() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await api.DELETE('/me/saved-meals/{id}', {
        params: { path: { id } },
      });
      if (error) throw new Error('Failed to delete saved meal');
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: SAVED_MEALS_KEY });
      void qc.invalidateQueries({ queryKey: RECENTS_KEY });
    },
  });
}
